/**
 * Een potje dat in deze browser draait.
 *
 * Dit is de enige plek in `packages/web` die de volledige spelstand kent. Het scherm ziet
 * hem niet: dat krijgt een `GameView` en stuurt zetten terug, meer niet.
 *
 * Die knip is er niet uit netheid maar omdat het straks moet. Zodra een potje op een
 * server draait, mág de volledige stand niet naar de browser — daar zitten de handkaarten
 * van je tegenstander in. Wat hier staat krijgt dan een tweelingbroer die precies hetzelfde
 * aanbiedt maar over een verbinding praat, en het scherm merkt het verschil niet. Daarom
 * is de vorm hieronder bewust die van een verbinding: je *vraagt* om een weergave, je
 * *stuurt* een zet, en je krijgt een antwoord dat ook "nee" kan zijn.
 *
 * Wat hier dus met opzet **niet** in zit: iets weten over knoppen, plaatjes of animaties.
 * En wat er in het scherm met opzet niet meer in zit: `GameState`, `perform`,
 * `isLegalAction` en `chooseAction`.
 */
import { copiesOf, eventCards, expansionCards, type Seat } from '@catan/contracts';
import { chooseAction } from '@catan/ai';
import type { Zichtbarezet } from './afspelen.js';
import {
  aanZet,
  createGame,
  createRng,
  isLegalAction,
  perform,
  viewFor,
  START_KAARTEN,
  type Action,
  type GameState,
  type GameView,
} from '@catan/rules';

const CARD_IDS = copiesOf(expansionCards());
const EVENT_IDS = copiesOf(eventCards());

/** Jij zit altijd op stoel 0, je tegenstander op stoel 1. */
export const JIJ: Seat = 0;
export const COMPUTER: Seat = 1;

/** Het antwoord op een zet. `ok: false` betekent: er is niets gebeurd. */
export interface Zetuitkomst {
  readonly ok: boolean;
  /** Waarom het niet mocht. Alleen gevuld als `ok` onwaar is. */
  readonly reden?: string;
  /** Wat er in het verslag bij kwam, oudste eerst. */
  readonly log: readonly string[];
  /**
   * Wat er ná jouw zet nog gebeurde — de beurt van de computer, op de server gespeeld.
   *
   * Alleen bij een potje op de server; hiernaast in het geheugen speelt de computer via
   * `computerZet()` en loopt de presentatie langs `naZet`. Jouw eigen zet zit er niet in:
   * die heb je net al zien gebeuren.
   */
  readonly zetten?: readonly Zichtbarezet[] | undefined;
}

/** Wat de computer deed, of niets als hij niet aan zet was of vastzit. */
export interface Computerzet {
  readonly actie: Action;
  readonly log: readonly string[];
}

export interface PotjeOpties {
  readonly seed: string;
  readonly tegenstander: string;
  readonly moeilijkheid: string;
  /**
   * Verdeel je je startlandschappen zelf?
   *
   * Zo ja, dan begint je bord **leeg** — precies wat het origineel doet als je op "ja"
   * klikt. De computer verdeelt nooit zelf.
   */
  readonly zelfVerdelen: boolean;
}

export interface Potje {
  readonly seed: string;
  readonly opties: PotjeOpties;
  /** De weergave voor jou. Alles wat het scherm mag weten, en niets meer. */
  weergave(): GameView;
  /** Het verslag, nieuwste eerst. */
  verslag(): readonly string[];
  /** Een regel in het verslag zetten die niet van een zet komt. */
  meld(regel: string): void;
  /**
   * Jouw zet.
   *
   * Een belofte, ook hier waar het antwoord meteen klaar is. Anders zou het scherm twee
   * vormen moeten kennen — één voor een potje in deze browser en één voor een potje op de
   * server — en dat is precies het onderscheid dat het niet hoort te maken.
   */
  zet(action: Action): Promise<Zetuitkomst>;
  /** Is de computer nu aan zet? */
  computerAanZet(): boolean;
  /** Laat de computer zetten. `undefined` = hij was niet aan zet of wist niets. */
  computerZet(): Computerzet | undefined;
  /**
   * De stand rechtstreeks aanpassen — alleen voor de toetshaak.
   *
   * Staat er met opzet als **optioneel** in: een potje dat op een server draait heeft deze
   * methode niet, en kán hem niet hebben. Daar is de stand niet van de browser, en een
   * knop om hem te veranderen zou precies het gat zijn waarmee je jezelf twaalf punten
   * geeft. `toetshaak.ts` controleert dus of hij bestaat, en werkt alleen op een potje dat
   * hier in de browser draait.
   */
  pasAan?(verander: (state: GameState) => GameState): void;
  /**
   * Dit potje laten rusten.
   *
   * Een potje op de server vraagt geregeld of de ander al iets gedaan heeft; dat moet
   * stoppen zodra het niet meer in beeld staat. Een potje in deze browser heeft niets op
   * te ruimen, dus daar is hij er niet.
   */
  sluit?(): void;
  /**
   * Is je tegenstander al minuten weg?
   *
   * Alleen bij een potje tegen een mens op de server. De computer loopt niet weg, en een
   * potje in deze browser heeft geen ander die weg kán zijn.
   */
  anderWeg?(): boolean;
  /**
   * Het potje opgeven omdat de ander weg is.
   *
   * Geeft een uitleg terug als het niet mocht — dat is met opzet geen stille mislukking:
   * "de tegenstander is er nog" is precies wat je wilt lezen als je te vroeg drukt.
   */
  geefOp?(): Promise<string | undefined>;
  /**
   * Het figuur dat jij in dit potje speelt, zoals de server het bewaart.
   *
   * Alleen bij een potje op de server. Nodig bij het hervatten in een nieuw tabblad: de
   * browser weet dan niet meer wat je koos, de server wel.
   */
  figuur?(): string;
  /**
   * Het figuur van je tegenstander, zodat hij niet als Louis in beeld komt.
   *
   * Alleen bij een potje op de server, en alleen als die het meestuurt. Hiernaast in het
   * geheugen is de tegenstander de computer, en die heeft zijn portret aan zijn naam.
   */
  tegenfiguur?(): string | undefined;
  /**
   * Hoe het potje er op de server voorstaat: `active`, `finished`, `abandoned`, of
   * `onbereikbaar` als de server het niet meer wil vertellen.
   */
  status?(): string;
  /**
   * Zit er een mens tegenover je?
   *
   * Alleen bij een potje op de server. Het eindscherm en de meldingen verschillen: een
   * oefenpotje heeft een soloscore, een partij tussen mensen niet.
   */
  tegenMens?(): boolean;
  /**
   * De toestand van de verbinding: `ok`, `haperend` of `weg`.
   *
   * Alleen bij een potje op de server; een lokaal potje hééft geen verbinding om over te
   * klagen. Het scherm gebruikt dit voor een indicator, zodat een stilte nooit stil is.
   */
  verbinding?(): 'ok' | 'haperend' | 'weg';
}

export function nieuwPotje(opties: PotjeOpties): Potje {
  let state = createGame(
    {
      seed: opties.seed,
      names: ['Jij', opties.tegenstander],
      // In het solospel begin jij. De kern kan het lot laten beslissen (handboek p. 10) en
      // doet dat ook zolang `startingSeat` wegblijft, maar aan deze tafel is het niet leuk
      // om te openen met kijken hoe de computer een beurt speelt.
      startingSeat: JIJ,
      zelfVerdelen: [opties.zelfVerdelen, false],
    },
    CARD_IDS,
    EVENT_IDS,
  );
  let rng = createRng(`${opties.seed}-ai`);
  const log: string[] = ['Een nieuw potje begint.'];

  // De computer kiest zijn drie startkaarten meteen: voor hem is dat geen beslissing waar
  // je bij wilt zitten, en zonder die kaarten kan hij de eerste beurten niets.
  for (let stap = 0; stap < 20 && state.players[COMPUTER].hand.length < START_KAARTEN; stap++) {
    const keuze = chooseAction(state, COMPUTER, rng, { moeilijkheid: opties.moeilijkheid });
    rng = keuze.rng;
    if (keuze.action === undefined || keuze.action.type === 'opzet-klaar') break;
    state = perform(state, COMPUTER, keuze.action).state;
  }

  /** Voert een zet uit en schrijft het verslag bij. */
  const uitvoeren = (seat: Seat, action: Action): readonly string[] => {
    const uitkomst = perform(state, seat, action);
    state = uitkomst.state;
    log.unshift(...[...uitkomst.log].reverse());
    return uitkomst.log;
  };

  return {
    seed: opties.seed,
    opties,

    weergave: () => viewFor(state, JIJ),
    verslag: () => log,
    meld: (regel) => log.unshift(regel),

    zet(action) {
      // Dit is altijd jóuw zet. Hem toeschrijven aan "wie aan de beurt is" ging ooit mis
      // zodra de computer als eerste aan zet was: dan werd je klik namens hem uitgevoerd,
      // en dat merkte je pas als hij iets niet mocht wat jij wél mocht.
      const oordeel = isLegalAction(state, JIJ, action);
      if (!oordeel.ok) return Promise.resolve({ ok: false, reden: oordeel.reason, log: [] });
      return Promise.resolve({ ok: true, log: uitvoeren(JIJ, action) });
    },

    computerAanZet() {
      if (state.winner !== undefined) return false;
      // Wie er aan zet is, is regelkennis en staat in `aanZet`: bij een openstaand aanbod
      // is dat de ánder, want een aanbod doe je in je eigen beurt. Dat dekt allebei de
      // kanten. Ligt er een aanbod ván de computer, dan wacht hij dus op jouw antwoord —
      // zonder die rem speelde hij door en sloot hij zijn beurt af, zodat zijn eigen
      // aanbod alweer weg was voordat je het gelezen had.
      return aanZet(state) === COMPUTER;
    },

    pasAan(verander) {
      state = verander(state);
    },

    computerZet() {
      if (!this.computerAanZet()) return undefined;
      const keuze = chooseAction(state, COMPUTER, rng, { moeilijkheid: opties.moeilijkheid });
      rng = keuze.rng;
      if (keuze.action === undefined) return undefined;
      return { actie: keuze.action, log: uitvoeren(COMPUTER, keuze.action) };
    },
  };
}
